import { FC } from "react";

import CoverageLastCheckIcon from "@/assets/icons/coverage-last-check.svg";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import { HouseholdMemberType } from "@/domain/householdMember";
import { useTranslator } from "@/hooks/useTranslate";
import { getApplicationTypeLabel } from "@/utils/applications";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";

import { CoverageStatusIcon } from "./CoverageStatusIcon";

type CoverageStatusCardPropsType = {
  householdMember: HouseholdMemberType;
};

export const HouseholdMemberSummaryCard: FC<CoverageStatusCardPropsType> = ({
  householdMember,
}) => {
  const { t } = useTranslator();
  const displayName = householdMember.firstName
    ? `${formatHouseholdMemberName(householdMember)}`
    : t("common:householdMember");

  const coverageStatus = householdMember.coverage?.status ?? "UNKNOWN";
  const lastChecked = householdMember.coverage?.lastChecked;
  const lastCheckDate =
    coverageStatus !== "CHECKING" && lastChecked
      ? dateAsDayjs(lastChecked).format(
          t("native:coverage.lastCheckDateFormat"),
        )
      : null;
  const coveredPrograms = householdMember.coverage?.coveredPrograms ?? [];
  const coveredApplicationTypes = [
    ...new Set(coveredPrograms.map(p => p.applicationType)),
  ];

  const coverageText = () => {
    switch (coverageStatus) {
      case "CHECKING":
        return t("native:coverage.status.checking");
      case "UNKNOWN":
        return t("native:coverage.status.unknown");
      case "NOT_COVERED":
        return t("native:coverage.status.notCovered");
      case "COVERED":
        return t("native:coverage.status.coveredBy", {
          programs: coveredApplicationTypes
            .map(p => t(getApplicationTypeLabel(p)))
            .join(t("common:listSeparator")),
        });
    }
  };

  const displayAge = householdMember.ageRange
    ? t(`common:age.range.${householdMember.ageRange}`)
    : householdMember.dateOfBirth
      ? t("common:age.years", {
          years: dateAsDayjs().diff(
            dateAsDayjs(householdMember.dateOfBirth),
            "year",
          ),
        })
      : t("common:age.unknown");

  return (
    <VnLink
      href={{
        pathname: "/(authed)/household/[householdMemberId]",
        params: { householdMemberId: householdMember.id },
      }}
      asChild>
      <VnCard className="w-full grow gap-3 md:max-w-44">
        <VnBox className="flex flex-col gap-1">
          <VnText variant="headerThree">{displayName}</VnText>
          <VnText variant="mdMedium" className="text-muted-foreground">
            {displayAge}
          </VnText>
        </VnBox>
        <VnBox className="flex flex-col gap-2">
          <VnBox className="flex flex-row items-start gap-1.5">
            <CoverageStatusIcon coverageStatus={coverageStatus} />
            <VnText className="flex-1" variant="md">
              {coverageText()}
            </VnText>
          </VnBox>
          {lastCheckDate && (
            <VnBox className="flex flex-row items-start gap-1.5">
              <CoverageLastCheckIcon className="h-5 w-5 shrink-0" />
              <VnText
                className="flex-1"
                variant="md"
                i18nKey="native:coverage.lastCheckWithDate"
                i18nOptions={{ date: lastCheckDate }}
              />
            </VnBox>
          )}
        </VnBox>
      </VnCard>
    </VnLink>
  );
};
